import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataContext } from '../contexts/DataContext';
import Pagination from '../utils/Pagination';
import logo from "../wisdom.png";
import Select from 'react-select';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoneyBillWave, faFileInvoiceDollar, faHandHoldingUsd } from '@fortawesome/free-solid-svg-icons';
import MiniDashboardPaymentRequests from './MinidashboardPaymentRequests';
import axios from 'axios';

//import MiniDashboardPaymentRequests from './MiniDashboardPaymentRequests';

const PaymentRequestGrid = () => {
    const { paymentRequests, fetchPaymentRequests } = useContext(DataContext);
    const [filteredRequests, setFilteredRequests] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [showDateRangePicker, setShowDateRangePicker] = useState(false);
    const [toggledStatuses, setToggledStatuses] = useState({});
    const [dateRange, setDateRange] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection',
        },
    ]);
    const navigate = useNavigate();

    // Initialize state for filters
    const [supplierOptions, setSupplierOptions] = useState([]);
    const [customerNameOptions, setCustomerNameOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [filters, setFilters] = useState({
        supplierNames: [],
        customerNames: [],
        statuses: [],
        createdDateRange: { start: null, end: null },
    });

    const handleDateRangeChange = (ranges) => {
        const { selection } = ranges;
        setDateRange([selection]);
        setFilters((prevFilters) => ({
            ...prevFilters,
            createdDateRange: {
                start: selection.startDate,
                end: selection.endDate,
            },
        }));
    };

    const sortRequests = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        } else {
            direction = 'ascending';
        }

        let sortedRequests = [...filteredRequests].sort((a, b) => {
            // Assuming all values are strings or numbers, adjust as necessary
            let aVal = a[key];
            let bVal = b[key];

            // For dates or custom sorting, add specific cases here

            if (aVal < bVal) {
                return direction === 'ascending' ? -1 : 1;
            }
            if (aVal > bVal) {
                return direction === 'ascending' ? 1 : -1;
            }
            return 0;
        });

        setFilteredRequests(sortedRequests);
        setSortConfig({ key, direction });
    };

    // Function to toggle the date range picker visibility
    const toggleDateRangePicker = () => {
        setShowDateRangePicker(!showDateRangePicker);
    };
    // Other states and hooks initialization...
    const toggleExpandedRow = (requestId) => {
        setExpandedRows(prev => {
            const newExpandedRows = new Set(prev);
            if (newExpandedRows.has(requestId)) {
                newExpandedRows.delete(requestId);
            } else {
                newExpandedRows.add(requestId);
            }
            return newExpandedRows;
        });
    };

    useEffect(() => {
        fetchPaymentRequests();
    }, [fetchPaymentRequests]);

    // Logic to create filter options
    useEffect(() => {
        if (paymentRequests) {
            // Unique suppliers
            const uniqueSuppliers = [...new Set(paymentRequests.map(request => request.supplier_name))];
            setSupplierOptions(uniqueSuppliers.map(supplier => ({ value: supplier, label: supplier })));

            // Unique customer names
            const uniqueCustomerNames = [...new Set(paymentRequests.map(request => request.customer_data.customer_name))];
            setCustomerNameOptions(uniqueCustomerNames.map(name => ({ value: name, label: name })));

            // Unique statuses
            const uniqueStatuses = [...new Set(paymentRequests.map(request => request.status))];
            setStatusOptions(uniqueStatuses.map(status => ({ value: status, label: status })));
        }
    }, [paymentRequests]);

    // Filter function to update the list of filteredRequests
    useEffect(() => {
        const filtered = paymentRequests.filter(request => {
            const matchesSupplier = !filters.supplierNames.length || filters.supplierNames.includes(request.supplier_name);
            const matchesCustomerName = !filters.customerNames.length || filters.customerNames.includes(request.customer_data.customer_name);
            const matchesStatus = !filters.statuses.length || filters.statuses.includes(request.status);
            const matchesDateRange = !filters.createdDateRange.start || (new Date(request.created_date) >= filters.createdDateRange.start && new Date(request.created_date) <= filters.createdDateRange.end);

            return matchesSupplier && matchesCustomerName && matchesStatus && matchesDateRange;
        });

        setFilteredRequests(filtered);
    }, [paymentRequests, filters]);


    const handleEditRequest = (request) => {
        if (request && request._id) {
            console.log(request);
            navigate('/paymentRequest', { state: { rowData: request, paymentRequestId: request._id } });
        }
    };

    const getStatusClasses = (status) => {
        const baseClasses = "inline-flex items-center justify-center rounded px-2 py-1 capitalize font-semibold";
        const statusClasses = {
            'open': "bg-yellow-200 text-yellow-800",
            'review': "bg-orange-200 text-orange-800",
            'payment_executed': "bg-purple-200 text-purple-800",
            'closed': "bg-green-200 text-green-800",
        };

        // Check if status is defined to avoid "Cannot read properties of undefined" error
        const statusKey = status ? status.toLowerCase() : 'undefined';
        return `${baseClasses} ${statusClasses[statusKey] || 'bg-gray-200 text-gray-800'}`; // Fallback styling for undefined status
    };

    const renderExpenseSection = (expenses, title, iconClass) => {
        // Calculate the total amount for the expense section
        const totalAmount = expenses.reduce((acc, expense) => acc + expense.amount, 0);

        return (
            <div className="flex flex-col flex-grow bg-blue-50 p-4 rounded-lg m-2">
                <div className="flex items-center mb-4">
                    <FontAwesomeIcon icon={iconClass} className="text-blue-500 mr-2" />
                    <h4 className="font-bold text-blue-700">{title}</h4>
                </div>
                {expenses.map((expense, index) => (
                    <div key={index} className="flex justify-between border-b border-gray-200 pb-2 mb-2">
                        <span className="text-sm">{expense.concept}</span>
                        <span className="text-sm font-bold">${expense.amount.toFixed(2)}</span>
                    </div>
                ))}
                <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between">
                        <span className="text-sm font-bold">Total</span>
                        <span className="text-sm font-bold">${totalAmount.toFixed(2)}</span>
                    </div>
                </div>
            </div>
        );
    };

    const getAgingClasses = (createdDate) => {
        const today = new Date();
        const created = new Date(createdDate);
        const differenceInTime = today.getTime() - created.getTime();
        const differenceInDays = differenceInTime / (1000 * 3600 * 24);

        if (differenceInDays <= 3) {
            return "bg-green-200 text-green-800"; // Light green background with darker green text for 1-3 days
        } else if (differenceInDays <= 5) {
            return "bg-yellow-200 text-yellow-800"; // Yellow background with darker yellow text for 3-5 days
        } else {
            return "bg-red-200 text-red-800"; // Red background with darker red text for more than 5 days
        }
    };

    const renderExpandedSection = (request) => {
        return (
            <div className="flex flex-wrap justify-between -mx-2 p-4 rounded-b">
                <div className="flex-1 min-w-0 px-2">
                    {renderExpenseSection(request.arrival_expenses, "Arrival Expenses", faMoneyBillWave)}
                </div>
                <div className="flex-1 min-w-0 px-2">
                    {renderExpenseSection(request.dispatch_expenses, "Dispatch Expenses", faFileInvoiceDollar)}
                </div>
                <div className="flex-1 min-w-0 px-2">
                    {renderExpenseSection(request.operation_closure_expenses, "Operation Closure Expenses", faHandHoldingUsd)}
                </div>
            </div>
        );
    };

    const calculateStatistics = (requests) => {
        const totalOpenRequests = requests.filter(req => req.status !== "closed").length;
        const totalClosedRequests = requests.filter(req => req.status === "closed").length; // Assuming 'Closed' is a status
        const totalRequestedAmount = requests.reduce((sum, req) => sum + req.total_payment_request, 0);
        const averageAging = requests.reduce((sum, req) => sum + ((new Date() - new Date(req.created_date)) / (1000 * 3600 * 24)), 0) / requests.length;

        return {
            totalOpenRequests,
            totalClosedRequests,
            totalRequestedAmount: `$${totalRequestedAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            averageAging
        };
    };

    const { totalOpenRequests, totalClosedRequests, totalRequestedAmount, averageAging } = calculateStatistics(paymentRequests);


    const renderDataGrid = () => {
        // Helper function to format total_to_pay
        const formatTotalToPay = (amount) => {
            return `$${amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
        };

        const handleStatusToggle = async (requestId) => {
            const currentStatus = toggledStatuses[requestId];
            const newStatus = currentStatus ? "review" : "open"; // Determine the new status based on the current one

            try {
                // Make the API call to update the status in the backend
                await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/payment_request/update_payment_request_status`, {
                    documentId: requestId,
                    newStatus: newStatus,
                });

                // Upon successful update, modify the local state to reflect the change
                // Clone the current state to avoid direct mutation
                const updatedRequests = filteredRequests.map(request => {
                    if (request._id === requestId) {
                        return { ...request, status: newStatus }; // Update the status of the relevant request
                    }
                    return request;
                });

                setFilteredRequests(updatedRequests); // Set the updated requests array as the new state

                // Update toggledStatuses to reflect the change
                setToggledStatuses(prevStatuses => ({
                    ...prevStatuses,
                    [requestId]: !currentStatus
                }));
            } catch (error) {
                console.error("Error updating payment request status:", error);
                // Optionally, handle the error, e.g., by displaying an error message to the user
            }
        };



        // Helper function to format created_date
        const formatDate = (dateString) => {
            const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' };
            return new Date(dateString).toLocaleDateString('en-US', options);
        };

        return (
            <div className="mt-4">
                {/* Header Row */}
                <div className="grid grid-cols-1 md:grid-cols-10 text-center font-semibold bg-gray-200 p-3 my-2 rounded-t-lg">
                    <div>Reference</div>
                    <div>Customer Name</div>
                    <div>Supplier Name</div>
                    <div>Status</div>
                    <div>Aging</div>
                    <div>Total to Pay</div>
                    <div>Created Date</div>
                    <div>Priority</div>
                    <div>Currency</div>
                    <div>Actions</div>
                </div>
                {/* Data Rows */}
                {filteredRequests.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((request, index) => (
                    <React.Fragment key={index}>
                        <div className="grid grid-cols-1 md:grid-cols-10 text-center text-xs font-semibold leading-5 bg-white p-2 my-2 rounded shadow flex items-center">
                            <div className="flex items-center justify-center">{request.traffic_reference}</div>
                            <div className="flex items-center justify-center">{request.customer_data.customer_name}</div>
                            <div className="flex items-center justify-center">{request.supplier_name}</div>
                            <div className={getStatusClasses(request.status)}>{request.status}</div>
                            <div className="flex items-center justify-center">
                                <span className={`rounded-full px-2 py-1 ${getAgingClasses(request.created_date)}`}>
                                    {Math.round((new Date() - new Date(request.created_date)) / (1000 * 60 * 60 * 24))} days
                                </span>
                            </div>
                            <div className="flex items-center justify-center">{formatTotalToPay(request.total_to_pay)}</div>
                            <div className="inline-flex items-center justify-center bg-purple-200 text-purple-800 rounded px-2 py-1">
                                {formatDate(request.created_date)}
                            </div>
                            <div className="flex items-center justify-center">{request.priority}</div>
                            <div className="flex items-center justify-center">{request.currency}</div>
                            <div className="flex justify-center gap-x-2">
                                <button
                                    onClick={() => toggleExpandedRow(request._id)}
                                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded"
                                >
                                    More Details
                                </button>
                                {request.status !== "closed" && ( // Conditionally render the Edit button
                                    <button onClick={() => handleEditRequest(request)} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded">
                                        Edit
                                    </button>
                                )}
                                <label className="flex items-center cursor-pointer">
                                    <div className="relative">
                                        <input
                                            type="checkbox"
                                            checked={request.status === "review"}
                                            className='sr-only'
                                            onChange={() => handleStatusToggle(request._id)}
                                        />
                                        <div className="w-10 h-4 rounded-full shadow-inner"
                                            style={{ backgroundColor: request.status === "review" ? '#f9cb9c' : '#d1d5db' }}></div>
                                        <div className="dot absolute w-6 h-6 bg-white rounded-full shadow -left-1 -top-1 transition"
                                            style={{ transform: request.status === "review" ? 'translateX(100%)' : 'translateX(0)', backgroundColor: request.status === "review" ? '#9ca3af' : '#9ca3af' }}></div>
                                    </div>
                                </label>

                            </div>
                            {expandedRows.has(request._id) && (
                                <div className="md:col-span-8 p-1 my-2 rounded">
                                    {renderExpandedSection(request)}
                                </div>
                            )}
                        </div>
                    </React.Fragment>
                ))
                }
                {
                    filteredRequests.length === 0 && (
                        <div className="text-center my-4">No payment requests found.</div>
                    )
                }
            </div >
        );
    };



    return (
        <>
            <nav className="w-full flex justify-between items-center px-6 py-4 bg-white shadow">
                <img src={logo} alt="Logo" className="h-12 mr-2" />
                <h1 className="text-xl font-semibold text-gray-700">Payment Request List</h1>
            </nav>
            <div className="container mx-auto p-6 bg-gray-100">
                <div className="flex gap-4 p-1 relative">
                    <Select
                        options={supplierOptions}
                        isMulti
                        onChange={selectedOptions => setFilters(prev => ({ ...prev, supplierNames: selectedOptions.map(option => option.value) }))}
                        placeholder="Filter by Supplier Name"
                    />
                    <Select
                        options={customerNameOptions}
                        isMulti
                        onChange={selectedOptions => setFilters(prev => ({ ...prev, customerNames: selectedOptions.map(option => option.value) }))}
                        placeholder="Filter by Customer Name"
                    />
                    <Select
                        options={statusOptions}
                        isMulti
                        onChange={selectedOptions => setFilters(prev => ({ ...prev, statuses: selectedOptions.map(option => option.value) }))}
                        placeholder="Filter by Status"
                    />
                    <button
                        onClick={toggleDateRangePicker}
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded"
                    >
                        Select Date Range {/* This button toggles the date picker */}
                    </button>
                    {showDateRangePicker && (
                        <div className="absolute top-full left-0 z-50 bg-white mt-1 rounded-md shadow-lg">
                            {/* Date range picker */}
                            <DateRangePicker
                                ranges={[dateRange[0]]}
                                onChange={handleDateRangeChange}
                                className="date-range"
                            />
                        </div>
                    )}
                </div>

                <MiniDashboardPaymentRequests
                    totalOpen={totalOpenRequests}
                    totalClosed={totalClosedRequests}
                    totalRequestedAmount={totalRequestedAmount}
                    averageAging={averageAging}
                />
                {/* Filter and MiniDashboard implementations */}
                <Pagination
                    total={filteredRequests.length}
                    pageSize={pageSize}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                />
                <div className="overflow-x-auto">
                    {renderDataGrid()}
                </div>
            </div>
        </>
    );
};

export default PaymentRequestGrid;
